// React/finance-app/src/features/gateway-management/UserGatewaysManagement.jsx
/**
 * Staff component for managing user-owned gateways and user access
 * - Tab 1: Manage user gateway access (grant/revoke)
 * - Tab 2: Manage user-owned gateways (CRUD)
 */
import React, { useState, useEffect, Fragment, useCallback } from 'react';
import api, {
  fetchGateways,
  createGateway,
  updateGateway,
  deleteGateway,
  listUsersWithGatewayAccess,
  grantGatewayAccess,
  revokeGatewayAccess,
  fetchPortsByGateway,
  fetchMessagesIndexedByPort,
} from '../../api';
import GatewayPortsTable from './components/GatewayPortsTable';
import { fetchAdvicePasswords } from '../../api';
import {
  usePortActivationStatus,
  PortActivationBadge,
  getActivationButtonState,
} from '../../shared/portActivation';

function UserGatewaysManagement() {
  const [activeTab, setActiveTab] = useState('access'); // 'access' or 'gateways'
  
  // Gateway management state
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingGateway, setEditingGateway] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  // Access management state
  const [users, setUsers] = useState([]);
  const [accessLoading, setAccessLoading] = useState(true);
  
  // Ports view state
  const [expandedGateway, setExpandedGateway] = useState(null);
  const [gatewayPorts, setGatewayPorts] = useState({});
  const [gatewayPortsLoading, setGatewayPortsLoading] = useState({});
  const [advicePasswords, setAdvicePasswords] = useState([]);
  const [gatewayPortMessages, setGatewayPortMessages] = useState({}); // gatewayId -> port messages map

  const { portStatus, refresh: refreshPortActivation } = usePortActivationStatus({
    scope: 'staff',
  });
  
  const [formData, setFormData] = useState({
    name: '',
    host: '',
    port: 80,
    username: '',
    password: '',
    model: '',
    port_count: 64,
    is_active: true,
    is_in_house: false,
    owner_user_code: '', // Staff can assign to a user
  });

  useEffect(() => {
    if (activeTab === 'access') {
      fetchUsersWithAccess();
    } else {
      fetchUserGateways();
    }
  }, [activeTab]);

  const fetchUsersWithAccess = async () => {
    try {
      setAccessLoading(true);
      setError(null);
      const data = await listUsersWithGatewayAccess();
      setUsers(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch users');
    } finally {
      setAccessLoading(false);
    }
  };

  const fetchUserGateways = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchGateways(true); // Only user-owned gateways
      setGateways(data);
      loadAdvicePasswords();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch gateways');
    } finally {
      setLoading(false);
    }
  };

  // Fetch advice passwords for port matching
  const loadAdvicePasswords = async () => {
    try {
      const data = await fetchAdvicePasswords();
      setAdvicePasswords(data || []);
    } catch (err) {
      console.error('Error fetching advice passwords:', err);
    }
  };

  // Toggle gateway ports view
  const toggleGatewayPorts = async (gatewayId) => {
    if (expandedGateway === gatewayId) {
      setExpandedGateway(null);
    } else {
      setExpandedGateway(gatewayId);
      if (!gatewayPorts[gatewayId]) {
        // Fetch ports, status, and messages for this gateway
        setGatewayPortsLoading(prev => ({ ...prev, [gatewayId]: true }));
        try {
          const [ports] = await Promise.all([
            fetchPortsByGateway(gatewayId),
          ]);
          setGatewayPorts(prev => ({ ...prev, [gatewayId]: ports }));
          
          // Load messages for all ports
          const messageMap = await fetchMessagesIndexedByPort(ports, 10);
          setGatewayPortMessages(prev => ({ ...prev, [gatewayId]: messageMap }));
        } catch (err) {
          console.error('Error fetching gateway data:', err);
          setGatewayPorts(prev => ({ ...prev, [gatewayId]: [] }));
        } finally {
          setGatewayPortsLoading(prev => ({ ...prev, [gatewayId]: false }));
        }
      }
    }
  };

  const handleGrantAccess = async (userCode) => {
    try {
      setError(null);
      await grantGatewayAccess(userCode);
      setSuccess(`Gateway access granted to ${userCode}`);
      fetchUsersWithAccess();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to grant access');
    }
  };

  const handleRevokeAccess = async (userCode) => {
    try {
      setError(null);
      await revokeGatewayAccess(userCode);
      setSuccess(`Gateway access revoked from ${userCode}`);
      fetchUsersWithAccess();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to revoke access');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      setSuccess(null);

      if (editingGateway) {
        await updateGateway(editingGateway.id, formData);
        setSuccess('Gateway updated successfully');
      } else {
        await createGateway(formData);
        setSuccess('Gateway created successfully');
      }

      resetForm();
      fetchUserGateways();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save gateway');
    }
  };

  const handleEdit = (gateway) => {
    setEditingGateway(gateway);
    setFormData({
      name: gateway.name,
      host: gateway.host,
      port: gateway.port,
      username: gateway.username,
      password: gateway.password,
      model: gateway.model || '',
      port_count: gateway.port_count || 64,
      is_active: gateway.is_active,
      is_in_house: gateway.is_in_house,
      owner_user_code: gateway.owner_user_code || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (gatewayId) => {
    if (!window.confirm('Are you sure you want to delete this gateway? This will also delete all associated ports.')) {
      return;
    }

    try {
      setError(null);
      await deleteGateway(gatewayId);
      setSuccess('Gateway deleted successfully');
      fetchUserGateways();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete gateway');
    }
  };

  const resetForm = () => {
    setEditingGateway(null);
    setShowForm(false);
    setFormData({
      name: '',
      host: '',
      port: 80,
      username: '',
      password: '',
      model: '',
      port_count: 64,
      is_active: true,
      is_in_house: false,
      owner_user_code: '',
    });
  };

  const getPortStatusMap = useCallback(
    (gatewayId) => {
      const ports = gatewayPorts[gatewayId] || [];
      const map = {};
      ports.forEach((port) => {
        if (port?.id && portStatus[port.id]) {
          map[port.id] = portStatus[port.id];
        }
      });
      return map;
    },
    [gatewayPorts, portStatus]
  );

  const getButtonStateForGateway = useCallback(
    (gatewayId) => (port) => {
      const state = getActivationButtonState(port, getPortStatusMap(gatewayId));
      if (state.text === 'Activating…') {
        return {
          ...state,
          text: (
            <>
              <span className="spinner-border spinner-border-sm me-1" />
              Activating…
            </>
          ),
        };
      }
      return state;
    },
    [getPortStatusMap]
  );

  const getStatusBadgeForGateway = useCallback(
    (gatewayId) => (port) => (
      <PortActivationBadge
        statusEntry={getPortStatusMap(gatewayId)[port?.id]}
        compact
        showTrafficLight={false}
      />
    ),
    [getPortStatusMap]
  );

  const handleActivatePort = async (portId, phoneNumber) => {
    try {
      const { activatePortById } = await import('../../api');
      await activatePortById(portId, phoneNumber);
      refreshPortActivation();
    } catch (err) {
      console.error('Error activating port:', err);
      setError(err.response?.data?.detail || err.message || 'Activation failed');
    }
  };

  const handleDeletePort = async (portId) => {
    if (!window.confirm('Are you sure you want to delete this port? This cannot be undone.')) return;
    try {
      await api.delete(`/ports/${portId}`);
      // Refresh ports for the gateway
      const gateway = gateways.find(g => gatewayPorts[g.id]?.some(p => p.id === portId));
      if (gateway) {
        const ports = await fetchPortsByGateway(gateway.id);
        setGatewayPorts(prev => ({ ...prev, [gateway.id]: ports }));
      }
      setSuccess('Port deleted successfully');
    } catch (err) {
      console.error('Error deleting port:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to delete port');
    }
  };

  // Auto-clear messages
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);


  return (
    <div className="container-fluid px-3 py-2" style={{ fontSize: '13px' }}>
      <h4 className="mb-3" style={{ fontSize: '18px' }}>
        User Gateway Management
      </h4>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'access' ? 'active' : ''}`}
            onClick={() => setActiveTab('access')}
            style={{ cursor: 'pointer', border: 'none', background: 'none' }}
          >
            Manage Access
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'gateways' ? 'active' : ''}`}
            onClick={() => setActiveTab('gateways')}
            style={{ cursor: 'pointer', border: 'none', background: 'none' }}
          >
            User Gateways
          </button>
        </li>
      </ul>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {success && <div className="alert alert-success py-2">{success}</div>}

      {/* Access Management Tab */}
      {activeTab === 'access' && (
        <div>
          <h5 className="mb-3">Grant/Revoke Gateway Access</h5>
          <p className="text-muted mb-3">
            Grant or revoke access to the gateway management interface for users.
            Users with access can create and manage their own gateways.
          </p>
          
          {accessLoading ? (
            <div>Loading users...</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-striped table-bordered">
                <thead>
                  <tr>
                    <th>User Code</th>
                    <th>Full Name</th>
                    <th>Has Access</th>
                    <th>Has Gateway</th>
                    <th>Gateway Name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.user_code}>
                        <td><strong>{user.user_code}</strong></td>
                        <td>{user.full_name || '-'}</td>
                        <td>
                          <span className={`badge ${user.has_gateway_access ? 'bg-success' : 'bg-secondary'}`}>
                            {user.has_gateway_access ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${user.has_gateway ? 'bg-info' : 'bg-secondary'}`}>
                            {user.has_gateway ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>{user.gateway_name || '-'}</td>
                        <td>
                          {user.has_gateway_access ? (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRevokeAccess(user.user_code)}
                            >
                              Revoke Access
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleGrantAccess(user.user_code)}
                            >
                              Grant Access
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Gateway Management Tab */}
      {activeTab === 'gateways' && (
        <div>
          <h5 className="mb-3">User-Owned Gateways</h5>
          
          {loading ? (
            <div>Loading gateways...</div>
          ) : (
            <>
              <div className="mb-3">
        <button
          className="btn btn-primary btn-sm"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          disabled={showForm && !editingGateway}
        >
          {editingGateway ? 'Cancel Edit' : 'Add New User Gateway'}
        </button>
      </div>

      {showForm && (
        <div className="card mb-3">
          <div className="card-body">
            <h5 className="card-title">{editingGateway ? 'Edit Gateway' : 'Create New Gateway'}</h5>
            <form onSubmit={handleSubmit}>
              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label">Gateway Name *</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Owner User Code *</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="owner_user_code"
                    value={formData.owner_user_code}
                    onChange={handleInputChange}
                    placeholder="e.g., KD1, HD1"
                    required
                  />
                  <small className="text-muted">Leave empty for company-owned gateway</small>
                </div>
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label">Host/IP *</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="host"
                    value={formData.host}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Port *</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    name="port"
                    value={formData.port}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label">Username *</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Password *</label>
                  <input
                    type="password"
                    className="form-control form-control-sm"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label">Model</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Port Count</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    name="port_count"
                    value={formData.port_count}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label">In House</label>
                  <select 
                    className="form-control form-control-sm" 
                    name="is_in_house"
                    onChange={handleInputChange}
                  >
                    <option selected={formData.is_in_house === false} value={0} >No</option>
                    <option selected={formData.is_in_house === true} value={1} >Yes</option>
                  </select>
                </div>
              </div>

              <div className="mb-2">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label">Active</label>
                </div>
              </div>

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary btn-sm">
                  {editingGateway ? 'Update Gateway' : 'Create Gateway'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-sm table-striped table-bordered">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Owner User Code</th>
              <th>Host</th>
              <th>Port</th>
              <th>Model</th>
              <th>Port Count</th>
              <th>Active</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {gateways.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center text-muted">
                  No user-owned gateways found
                </td>
              </tr>
            ) : (
              gateways.map((gateway) => (
                <Fragment key={gateway.id}>
                  <tr>
                    <td>{gateway.id}</td>
                    <td>{gateway.name}</td>
                    <td>
                      <span className="badge bg-info">{gateway.owner_user_code || 'N/A'}</span>
                    </td>
                    <td>{gateway.host}</td>
                    <td>{gateway.port}</td>
                    <td>{gateway.model || '-'}</td>
                    <td>{gateway.port_count || 64}</td>
                    <td>
                      <span className={`badge ${gateway.is_active ? 'bg-success' : 'bg-secondary'}`}>
                        {gateway.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {gateway.created_at
                        ? new Date(gateway.created_at).toLocaleDateString()
                        : '-'}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-info me-1"
                        onClick={() => toggleGatewayPorts(gateway.id)}
                        title="View/Edit Ports"
                      >
                        {expandedGateway === gateway.id ? 'Hide' : 'View'} Ports
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary me-1"
                        onClick={() => handleEdit(gateway)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(gateway.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                  {expandedGateway === gateway.id && (
                    <tr>
                      <td colSpan="10" style={{ padding: 0, border: 'none' }}>
                        <div style={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                          {gatewayPortsLoading[gateway.id] ? (
                            <div className="text-center">Loading ports...</div>
                          ) : (
                            <GatewayPortsTable
                              gatewayName={gateway.name}
                              ports={gatewayPorts[gateway.id] || []}
                              advicePasswords={advicePasswords}
                              portStatus={getPortStatusMap(gateway.id)}
                              portMessages={gatewayPortMessages[gateway.id] || {}}
                              portCountdowns={{}}
                              clearPortMessage={(portId) => {
                                setGatewayPortMessages(prev => {
                                  const next = { ...prev };
                                  if (next[gateway.id]) {
                                    const updated = { ...next[gateway.id] };
                                    delete updated[portId];
                                    next[gateway.id] = updated;
                                  }
                                  return next;
                                });
                              }}
                              getButtonState={getButtonStateForGateway(gateway.id)}
                              getStatusBadge={getStatusBadgeForGateway(gateway.id)}
                              onActivatePort={handleActivatePort}
                              onReportProblem={() => {}}
                              onEditPort={(port) => {
                                // Navigate to port management
                                window.location.href = `/ports?port_id=${port.id}`;
                              }}
                              onDeletePort={handleDeletePort}
                              isStaff={true}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default UserGatewaysManagement;

